import { GET_SELLER_UNITS_AND_PROJECTS_LIST } from "@/gql/sup";
import type { Filters, ListItem } from "@/types/search";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface Pagination {
	page: number;
	pageSize: number;
	total: number;
}

export interface ListDataState {
	listData: ListItem[];
	isListDataFetching: boolean;
	pagination: Pagination;
	setListData: (data: ListItem[]) => void;
	setIsListDataFetching: (fetching: boolean) => void;
	setPagination: (pagination: Pagination) => void;
	fetchListData: (filters: Filters, skip?: number, limit?: number) => Promise<void>;
}

export function createListDataStore({
	initialListData = [],
	initialPagination = { page: 1, pageSize: 20, total: 0 },
}: { initialListData?: ListItem[]; initialPagination?: Pagination } = {}) {
	return create<ListDataState>()(
		devtools(
			subscribeWithSelector(
				immer(set => ({
					listData: initialListData,
					isListDataFetching: false,
					pagination: initialPagination,
					setListData: data =>
						set(state => {
							state.listData = data;
						}),
					setIsListDataFetching: fetching =>
						set(state => {
							state.isListDataFetching = fetching;
						}),
					setPagination: pagination =>
						set(state => {
							state.pagination = pagination;
						}),
					fetchListData: async (filters: Filters, skip = 0, limit = 20) => {
						set(state => {
							state.isListDataFetching = true;
						});

						try {
							const variables = {
								params: {
									list: {
										filters,
										skip,
										limit,
									},
								},
							};

							// Use dynamic import to avoid circular dependencies
							const { getApolloData } = await import("@/utils/apollo/get-data");

							const result = await getApolloData<{
								sellerUnitsAndProjects: {
									list: ListItem[];
									meta: {
										count: {
											total: number;
										};
									};
								};
							}>({
								query: GET_SELLER_UNITS_AND_PROJECTS_LIST,
								variables,
							});

							const listData = result.data?.sellerUnitsAndProjects?.list || [];
							const total = result.data?.sellerUnitsAndProjects?.meta?.count?.total || 0;

							set(state => {
								state.listData = listData;
								state.pagination = {
									page: Math.floor(skip / limit) + 1,
									pageSize: limit,
									total,
								};
								state.isListDataFetching = false;
							});
						} catch (_error) {
							set(state => {
								state.isListDataFetching = false;
							});
						}
					},
				})),
			),
			{ name: "searchPage-listData-store" },
		),
	);
}
